import React, { useState, useEffect, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import httpClient from "../../utils/httpClients";
import { usePlayer } from "../../../PlayerContext";

function PlayMusic() {
  const { id } = useParams();
  const navigate = useNavigate();
  const sliderRef = useRef(null);

  const [musics, setMusics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [likedSongs, setLikedSongs] = useState(new Set());
  const [sharePopup, setSharePopup] = useState({ open: false, song: null });
  const [genres, setGenres] = useState([]);
  const [genreInfo, setGenreInfo] = useState({ name: "", image: "" });

  // Playlist Modal
  const [playlistModal, setPlaylistModal] = useState({
    open: false,
    song: null,
  });
  const [playlists, setPlaylists] = useState([]);
  const [newPlaylist, setNewPlaylist] = useState({
    name: "",
    description: "",
    coverImage: null,
  });
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [playlistLoading, setPlaylistLoading] = useState(false);
  const [selectedPlaylists, setSelectedPlaylists] = useState(new Set());

  const {
    isPlaying,
    currentTrack,
    togglePlayPause,
    seek,
    duration,
    triggerQueueUpdate,
    playTrackById,
    stopPlayback,
    tracks,
  } = usePlayer();

  // === LOCAL PAGE PLAYBACK STATE ===
  const [selectedSongId, setSelectedSongId] = useState(null);
  const [hasInitiatedPlayback, setHasInitiatedPlayback] = useState(false);

  // Reset state on route id change so loaders/empty state appear immediately
  useEffect(() => {
    setSelectedSongId(null);
    setHasInitiatedPlayback(false);
    setMusics([]);
    setLoading(true);
    setError(null);
  }, [id]);

  // Auto-pick first song as default once songs arrive
  useEffect(() => {
    if (!selectedSongId && musics.length > 0) {
      setSelectedSongId(musics[0]._id);
    }
  }, [musics, selectedSongId]);

  const selectedSong =
    musics.find((song) => song._id === selectedSongId) || null;

  const displayTrack =
    selectedSong || (musics.length > 0 ? musics[0] : null);

  const isDisplayTrackPlaying =
    hasInitiatedPlayback &&
    isPlaying &&
    currentTrack?._id &&
    currentTrack?._id === displayTrack?._id;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  // Fetch Playlists
  const fetchPlaylists = async () => {
    try {
      const res = await httpClient.get("/user/playlist/get-playlists");
      if (res.data?.success) {
        setPlaylists(res.data.data.playlist || []);
      }
    } catch (err) {
      console.error("Error fetching playlists:", err);
    }
  };

  // Open Playlist Modal
  const openPlaylistModal = async (song, e) => {
    e.preventDefault();
    e.stopPropagation();
    setPlaylistModal({ open: true, song });
    setSelectedPlaylists(new Set());
    setShowCreateForm(false);
    await fetchPlaylists();
    setOpenDropdown(null);
  };

  // Toggle Playlist Selection
  const togglePlaylistSelection = (pid) => {
    setSelectedPlaylists((prev) => {
      const set = new Set(prev);
      set.has(pid) ? set.delete(pid) : set.add(pid);
      return set;
    });
  };

  // Add to Selected Playlists
  const addToSelectedPlaylists = async () => {
    if (selectedPlaylists.size === 0) return;
    setPlaylistLoading(true);
    try {
      for (const pid of selectedPlaylists) {
        await httpClient.post(
          `/user/playlist/add-item?playlistId=${pid}&itemId=${playlistModal.song._id}&itemType=Music`,
        );
      }
      setPlaylistModal({ open: false, song: null });
    } catch (err) {
      alert("Failed to add");
    } finally {
      setPlaylistLoading(false);
    }
  };

  // Create New Playlist
  const createNewPlaylist = async () => {
    if (!newPlaylist.name.trim()) return alert("Name required");
    setPlaylistLoading(true);
    const formData = new FormData();
    formData.append("name", newPlaylist.name);
    formData.append("description", newPlaylist.description);
    if (newPlaylist.coverImage)
      formData.append("coverImage", newPlaylist.coverImage);

    try {
      const res = await httpClient.post(
        "/user/playlist/create-playlist",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
      if (res.data?.success) {
        const newId = res.data.data.playlist._id;
        setSelectedPlaylists((prev) => new Set(prev).add(newId));
        await addToSelectedPlaylists();
      }
    } catch (err) {
      alert("Failed");
    } finally {
      setPlaylistLoading(false);
    }
  };

  // Fetch Genres
  const fetchGenresSongs = async () => {
    try {
      const res = await httpClient.get(`/user/music/get-themes`);
      if (res.data?.success) {
        const list = res.data.data.getThemes || [];
        setGenres(list);
        const match = list.find((g) => g._id === id);
        if (match) {
          setGenreInfo({
            name: match.name || "",
            image: match.themeImageUrl || "",
          });
        }
      }
    } catch (err) {
      console.error("Error fetching genres:", err);
    }
  };
  useEffect(() => {
    fetchGenresSongs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Queue Action Handler
  const handleQueueAction = async (action, payload = null) => {
    try {
      const body = { action };
      if (payload) {
        if (action === "addAllToQueue" && Array.isArray(payload)) {
          body.trackId = payload;
        } else if (action === "addPlaylistAndPlay") {
          body.playlistId = payload.playlistId;
          body.type = payload.type;
        } else if (payload.trackId) {
          body.trackId = payload.trackId;
        }
      }

      const res = await httpClient.post("user/queue/queue-controller", body);
      if (res.data?.success) {
        triggerQueueUpdate();
        return true;
      }
    } catch (error) {
      console.error("Queue action failed:", error);
      return false;
    }
    return false;
  };

  // Play Genre + Navigate (just navigate; destination page will own playback)
  const playAndNavigate = (genreId, type, navigateTo) => {
    navigate(navigateTo);
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Fetch Songs
  const fetchPlayMusics = async () => {
    try {
      setLoading(true);
      const res = await httpClient.get(`user/music/get-songs/${id}`);
      if (res.data?.success) {
        const data = res.data.data?.songs || [];
        setMusics(data);
        const liked = new Set();
        data.forEach((m) => m.isLike && liked.add(m._id));
        setLikedSongs(liked);
      } else {
        setError("Failed to load songs");
      }
    } catch (err) {
      setError("Error loading songs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchPlayMusics();
  }, [id]);

  // Like Song (optimistic update)
  const handleLikeSong = async (contentId, isLiked) => {
    const newLiked = !isLiked;

    setLikedSongs((prev) => {
      const set = new Set(prev);
      isLiked ? set.delete(contentId) : set.add(contentId);
      return set;
    });
    setMusics((prev) =>
      prev.map((m) => (m._id === contentId ? { ...m, isLiked: newLiked } : m)),
    );

    try {
      const res = await httpClient.post(
        `/user/play-history/like-content/${contentId}/${isLiked ? "dislike" : "like"}`,
        {},
        { skipToast: true, skipLoader: true },
      );
      if (!res.data?.success) {
        setLikedSongs((prev) => {
          const set = new Set(prev);
          newLiked ? set.delete(contentId) : set.add(contentId);
          return set;
        });
        setMusics((prev) =>
          prev.map((m) => (m._id === contentId ? { ...m, isLiked } : m)),
        );
      }
    } catch (err) {
      console.error("Like error:", err);
      setLikedSongs((prev) => {
        const set = new Set(prev);
        newLiked ? set.delete(contentId) : set.add(contentId);
        return set;
      });
      setMusics((prev) =>
        prev.map((m) => (m._id === contentId ? { ...m, isLiked } : m)),
      );
    }
  };

  // Play Song - clean state, then play via PlayerContext
  const playSong = async (song) => {
    if (!song?._id) return;

    setSelectedSongId(song._id);
    setHasInitiatedPlayback(true);

    if (currentTrack?._id === song._id) {
      if (!isPlaying) await togglePlayPause();
      return;
    }

    if (typeof stopPlayback === "function") {
      stopPlayback();
    }
    await playTrackById(song._id, "audio");
  };

  // Play All Songs
  const playAllSongs = async () => {
    if (musics.length === 0) return;

    const firstSong = musics[0];
    setSelectedSongId(firstSong._id);
    setHasInitiatedPlayback(true);

    if (currentTrack?._id === firstSong._id) {
      if (!isPlaying) await togglePlayPause();
      return;
    }

    if (typeof stopPlayback === "function") {
      stopPlayback();
    }
    const trackIds = musics.map((m) => m._id);
    await playTrackById(trackIds, "audio");
  };

  // Top Play/Pause button
  const handleTopPlayPauseClick = async () => {
    if (!displayTrack) return;
    setHasInitiatedPlayback(true);
    if (currentTrack?._id !== displayTrack._id) {
      await playSong(displayTrack);
      return;
    }
    await togglePlayPause();
  };

  // Add to Queue (Only this option)
  const addToQueue = async (trackId) => {
    const success = await handleQueueAction("add", { trackId });
    if (success) {
      console.log("Added to queue");
    }
  };

  // Play count is recorded only by PlayerContext (single source of truth)

  // Share Functions
  const generateShareableUrl = (songId) => {
    // `https://webmobrildemo.com/volumetv/music/${encodeURIComponent(songId)}`;
    return `${process.env.REACT_APP_FRONTEND_URL || window.location.origin}music/${encodeURIComponent(songId)}`;
  };

  const shareOnSocialMedia = (platform, song) => {
    const url = generateShareableUrl(song._id);
    const text = encodeURIComponent(song.title);
    const links = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      twitter: `https://twitter.com/intent/tweet?url=${url}&text=${text}`,
      whatsapp: `https://api.whatsapp.com/send?text=${text}%20${url}`,
      telegram: `https://t.me/share/url?url=${url}&text=${text}`,
    };
    window.open(links[platform], "_blank");
  };

  const copyLink = async () => {
    await navigator.clipboard.writeText(
      generateShareableUrl(sharePopup.song._id),
    );
    alert("Copied!");
  };

  const toggleDropdown = (id) =>
    setOpenDropdown((prev) => (prev === id ? null : id));

  // Close popups on outside click
  useEffect(() => {
    const handler = (e) => {
      if (!e.target.closest(".dropdown")) setOpenDropdown(null);
      if (
        !e.target.closest(".single-item__export") &&
        !e.target.closest(".share-popup")
      )
        setSharePopup({ open: false, song: null });
      if (
        !e.target.closest(".playlist-popup") &&
        !e.target.closest(".dropdown-item")
      )
        setPlaylistModal({ open: false, song: null });
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  if (loading)
    return (
      <div
        className="content-inner pb-0 container-fluid d-flex justify-content-center align-items-center"
        style={{ height: "70vh" }}
      >
        <div className="spinner-border text-primary"></div>
      </div>
    );

  if (error)
    return <div className="alert alert-danger text-center">{error}</div>;

  const isCurrentPlaying = (id) => currentTrack?._id === id;
  const queuePos = (id) => tracks.findIndex((t) => t._id === id) + 1 || null;

  // Slider Settings
  const sliderSettings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 6,
    slidesToScroll: 3,
    arrows: false,
    responsive: [
      { breakpoint: 1400, settings: { slidesToShow: 5 } },
      { breakpoint: 1200, settings: { slidesToShow: 4 } },
      { breakpoint: 992, settings: { slidesToShow: 3 } },
      { breakpoint: 768, settings: { slidesToShow: 2 } },
      { breakpoint: 576, settings: { slidesToShow: 1 } },
    ],
  };

  const pageTitle = genreInfo.name || "Genre Music";

  return (
    <div className="content-inner pb-0 container-fluid" id="page_layout">
      <div className="row row--grid">
        {/* Breadcrumb & Title */}
        <div className="col-12">
          <ul className="breadcrumb">
            <li className="breadcrumb__item">
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb__item">
              <Link to="/playlist">Playlist</Link>
            </li>
            <li className="breadcrumb__item breadcrumb__item--active">
              {pageTitle}
            </li>
          </ul>
        </div>
        <div className="col-12">
          <div className="main__title main__title--page">
            <h1>{pageTitle}</h1>
          </div>
        </div>

        {/* Main Release */}
        <div className="col-12">
          <div className="release">
            <div className="release__content">
              <div className="release__cover position-relative">
                <img
                  src={
                    displayTrack?.coverImageUrl ||
                    genreInfo.image ||
                    `${process.env.PUBLIC_URL}/img/covers/cover.svg`
                  }
                  alt={displayTrack?.title || pageTitle}
                  style={{
                    maxHeight: "340px",
                    width: "100%",
                    height: "340px",
                    objectFit: "contain",
                    background: "#1a1a1a",
                  }}
                  onError={(e) => {
                    e.currentTarget.src = `${process.env.PUBLIC_URL}/img/covers/cover3.jpg`;
                  }}
                />
              </div>
              <div className="release__stat">
                <span>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <path d="M20,13.18V11A8,8,0,0,0,4,11v2.18A3,3,0,0,0,2,16v2a3,3,0,0,0,3,3H8a1,1,0,0,0,1-1V14a1,1,0,0,0-1-1H6V11a6,6,0,0,1,12,0v2H16a1,1,0,0,0-1,1v6a1,1,0,0,0,1,1h3a3,3,0,0,0,3-3V16A3,3,0,0,0,20,13.18ZM7,15v4H5a1,1,0,0,1-1-1V16a1,1,0,0,1,1-1Zm13,3a1,1,0,0,1-1,1H17V15h2a1,1,0,0,1,1,1Z"></path>
                  </svg>{" "}
                  {musics.length} tracks
                </span>
                <span>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <path d="M20,13.18V11A8,8,0,0,0,4,11v2.18A3,3,0,0,0,2,16v2a3,3,0,0,0,3,3H8a1,1,0,0,0,1-1V14a1,1,0,0,0-1-1H6V11a6,6,0,0,1,12,0v2H16a1,1,0,0,0-1,1v6a1,1,0,0,0,1,1h3a3,3,0,0,0,3-3V16A3,3,0,0,0,20,13.18ZM7,15v4H5a1,1,0,0,1-1-1V16a1,1,0,0,1,1-1Zm13,3a1,1,0,0,1-1,1H17V15h2a1,1,0,0,1,1,1Z"></path>
                  </svg>{" "}
                  {musics
                    .reduce((a, b) => a + (b.playCount || 0), 0)
                    .toLocaleString()}
                </span>
              </div>
              <div className="d-flex gap-3 justify-content-between mt-3">
                <button
                  type="button"
                  className="release__buy open-modal"
                  onClick={handleTopPlayPauseClick}
                  disabled={!displayTrack}
                  style={{
                    cursor: displayTrack ? "pointer" : "not-allowed",
                    background: "transparent",
                    border: "none",
                  }}
                >
                  <i
                    className={
                      isDisplayTrackPlaying ? "fas fa-pause" : "fas fa-play"
                    }
                  ></i>{" "}
                  {isDisplayTrackPlaying ? "Pause" : "Play"}
                </button>
              </div>
            </div>

            {/* Songs List */}
            <div className="release__list">
              <ul className="main__list main__list--playlist main__list--dashbox">
                <h4 className="card-title text-capitalize episodes">
                  All Tracks
                </h4>
                {musics.length === 0 && (
                  <li
                    className="d-flex flex-column align-items-center justify-content-center text-muted py-5"
                    style={{ listStyle: "none" }}
                  >
                    <img
                      src={`${process.env.PUBLIC_URL}/img/covers/cover.svg`}
                      alt="No songs"
                      style={{
                        width: "120px",
                        height: "120px",
                        objectFit: "contain",
                        opacity: 0.6,
                        marginBottom: "16px",
                      }}
                    />
                    <h5 className="mb-1 text-white">No songs available</h5>
                    <p className="mb-0 small">
                      There are no tracks for this {pageTitle.toLowerCase()} yet.
                    </p>
                  </li>
                )}
                {musics.map((music, index) => {
                  const contentId = music._id;
                  const isLiked = likedSongs.has(contentId);
                  const playing = isCurrentPlaying(contentId);
                  const inQueue = queuePos(contentId);
                  const isDropdownOpen = openDropdown === contentId;

                  const ArtistId = music.artistIds?.[0] || music.artistId;
                  const AlbhumId = music.albumId || music.album?._id;
                  const isAlbhum = !!AlbhumId;

                  return (
                    <li
                      key={contentId}
                      className={`single-item list-1 ${
                        playing ? "active-episode" : ""
                      }`}
                      style={{
                        backgroundColor: playing ? "rgba(255,255,255,0.1)" : "",
                        borderLeft: playing
                          ? "3px solid #1DB954"
                          : "3px solid transparent",
                      }}
                    >
                      <div
                        className="single-item__cover"
                        onClick={() => playSong(music)}
                        style={{ cursor: "pointer" }}
                      >
                        <img
                          src={
                            music.coverImageUrl ||
                            `${process.env.PUBLIC_URL}/img/covers/cover.svg`
                          }
                          alt={music.title}
                        />
                        {playing && isPlaying ? (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            style={{ fill: "#1DB954" }}
                          >
                            <path d="M16,2a3,3,0,0,0-3,3V19a3,3,0,0,0,6,0V5A3,3,0,0,0,16,2Zm1,17a1,1,0,0,1-2,0V5a1,1,0,0,1,2,0ZM8,2A3,3,0,0,0,5,5V19a3,3,0,0,0,6,0V5A3,3,0,0,0,8,2ZM9,19a1,1,0,0,1-2,0V5A1,1,0,0,1,9,5Z" />
                          </svg>
                        ) : (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            style={{
                              fill: playing ? "#1DB954" : "#fff",
                            }}
                          >
                            <path d="M18.54,9L8.88,3.46a3.42,3.42,0,0,0-5.13,3V17.58A3.42,3.42,0,0,0,7.17,21a3.43,3.43,0,0,0,1.71-.46L18.54,15a3.42,3.42,0,0,0,0-5.92Zm-1,4.19L7.88,18.81a1.44,1.44,0,0,1-1.42,0,1.42,1.42,0,0,1-.71-1.23V6.42a1.42,1.42,0,0,1,.71-1.23A1.51,1.51,0,0,1,7.17,5a1.54,1.54,0,0,1,.71.19l9.66,5.58a1.42,1.42,0,0,1,0,2.46Z" />
                          </svg>
                        )}
                      </div>

                      <div className="single-item__title">
                        <h4>
                          {index + 1}. {music.title}
                          {playing && (
                            <span className="badge bg-success ms-2">
                              Playing
                            </span>
                          )}
                        </h4>
                        <span
                          onClick={() =>
                            ArtistId && navigate(`/art/${ArtistId}`)
                          }
                          style={{
                            cursor: ArtistId ? "pointer" : "default",
                            color: ArtistId ? "#1DB954" : "#888",
                          }}
                        >
                          {music.artist || "Unknown Artist"}
                        </span>
                      </div>

                      {/* Dropdown */}
                      <div className="dropdown">
                        <span
                          className={`d-flex text-white align-items-center h5 mb-0 ${
                            isDropdownOpen ? "show" : ""
                          }`}
                          onClick={() => toggleDropdown(contentId)}
                        >
                          <i className="fas fa-ellipsis-h"></i>
                        </span>
                        <div
                          className={`dropdown-menu dropdown-menu-end ${
                            isDropdownOpen ? "show" : ""
                          }`}
                          style={
                            isDropdownOpen
                              ? {
                                  position: "absolute",
                                  inset: "0px 0px auto auto",
                                  margin: "0px",
                                  transform: "translate(0px, 19px)",
                                }
                              : {}
                          }
                        >
                          <a
                            className="dropdown-item"
                            href="#!"
                            onClick={(e) => openPlaylistModal(music, e)}
                          >
                            <span className="material-icons">
                              library_music
                            </span>{" "}
                            Add To Playlist
                          </a>

                          {/* ONLY ADD TO QUEUE */}
                          <a
                            className="dropdown-item"
                            href="#!"
                            onClick={(e) => {
                              e.preventDefault();
                              addToQueue(contentId);
                              setOpenDropdown(null);
                            }}
                          >
                            <span className="material-icons">playlist_add</span>{" "}
                            Add To Queue
                          </a>

                          <a
                            className={`dropdown-item ${
                              !ArtistId ? "disabled" : ""
                            }`}
                            href="#!"
                            onClick={(e) => {
                              e.preventDefault();
                              if (ArtistId) {
                                navigate(`/art/${ArtistId}`);
                                setOpenDropdown(null);
                              }
                            }}
                          >
                            <span className="material-icons">music_note</span>{" "}
                            Go To Artist
                          </a>

                          <a
                            className={`dropdown-item ${
                              !isAlbhum ? "disabled" : ""
                            }`}
                            href="#!"
                            onClick={(e) => {
                              e.preventDefault();
                              if (isAlbhum) {
                                navigate(`/album/${AlbhumId}`);
                                setOpenDropdown(null);
                              }
                            }}
                          >
                            <span className="material-icons">album</span> Go To
                            Album
                          </a>
                        </div>
                      </div>

                      <a
                        href="#!"
                        className={`single-item__add ${isLiked ? "liked" : ""}`}
                        onClick={(e) => {
                          e.preventDefault();
                          handleLikeSong(contentId, isLiked);
                        }}
                      >
                        <i
                          className={
                            isLiked
                              ? "fas fa-heart text-danger"
                              : "far fa-heart"
                          }
                        ></i>
                      </a>

                      <a
                        href="#!"
                        className="single-item__export"
                        onClick={(e) => {
                          e.preventDefault();
                          setSharePopup({ open: true, song: music });
                        }}
                      >
                        <i className="fas fa-share"></i>
                      </a>

                      <span className="single-item__time">
                        {playing
                          ? `${formatDuration(seek)} / ${formatDuration(
                              duration,
                            )}`
                          : formatDuration(music.duration)}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>

        {/* GENRES SLIDER */}
        <div className="row mt-5">
          <div className="col-12">
            <div className="card-header mb-3 d-flex justify-content-between align-items-center">
              <h4 className="card-title text-capitalize">Top Song Genres</h4>
              <div className="common-album position-relative d-lg-block d-none">
                <button
                  className="swiper-button-prev"
                  onClick={() => sliderRef.current?.slickPrev()}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="19"
                    viewBox="0 0 18 19"
                    fill="none"
                  >
                    <path
                      d="M15.75 9.5C15.75 9.64918 15.6908 9.79226 15.5853 9.89775C15.4798 10.0032 15.3367 10.0625 15.1875 10.0625H4.17025L8.27298 14.1645C8.32525 14.2168 8.3667 14.2788 8.39499 14.3471C8.42327 14.4154 8.43783 14.4886 8.43783 14.5625C8.43783 14.6364 8.42327 14.7096 8.39499 14.7779C8.3667 14.8462 8.32525 14.9082 8.27298 14.9605C8.22072 15.0127 8.15868 15.0542 8.09039 15.0825C8.02211 15.1108 7.94893 15.1253 7.87502 15.1253C7.80111 15.1253 7.72792 15.1108 7.65964 15.0825C7.59135 15.0542 7.52931 15.0127 7.47705 14.9605L2.41455 9.89797C2.36225 9.84573 2.32076 9.78369 2.29245 9.7154C2.26414 9.64712 2.24957 9.57392 2.24957 9.5C2.24957 9.42608 2.26414 9.35288 2.29245 9.2846C2.32076 9.21631 2.36225 9.15427 2.41455 9.10203L7.47705 4.03953C7.58259 3.93398 7.72575 3.87469 7.87502 3.87469C8.02428 3.87469 8.16744 3.93398 8.27298 4.03953C8.37853 4.14508 8.43783 4.28823 8.43783 4.4375C8.43783 4.58677 8.37853 4.72992 8.27298 4.83547L4.17025 8.9375H15.1875C15.3367 8.9375 15.4798 8.99676 15.5853 9.10225C15.6908 9.20774 15.75 9.35082 15.75 9.5Z"
                      fill="#4A525F"
                    />
                  </svg>
                </button>
                <button
                  className="swiper-button-next"
                  onClick={() => sliderRef.current?.slickNext()}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="19"
                    viewBox="0 0 18 19"
                    fill="none"
                  >
                    <path
                      d="M15.5855 9.89797L10.523 14.9605C10.4174 15.066 10.2743 15.1253 10.125 15.1253C9.97573 15.1253 9.83258 15.066 9.72703 14.9605C9.62148 14.8549 9.56219 14.7118 9.56219 14.5625C9.56219 14.4132 9.62148 14.2701 9.72703 14.1645L13.8298 10.0625H2.8125C2.66332 10.0625 2.52024 10.0032 2.41475 9.89775C2.30926 9.79226 2.25 9.64918 2.25 9.5C2.25 9.35082 2.30926 9.20774 2.41475 9.10225C2.52024 8.99676 2.66332 8.9375 2.8125 8.9375H13.8298L9.72703 4.83547C9.62148 4.72992 9.56219 4.58677 9.56219 4.4375C9.56219 4.28823 9.62148 4.14508 9.72703 4.03953C9.83258 3.93398 9.97573 3.87469 10.125 3.87469C10.2743 3.87469 10.4174 3.93398 10.523 4.03953L15.5855 9.10203C15.6378 9.15427 15.6793 9.21631 15.7076 9.2846C15.7359 9.35288 15.7504 9.42608 15.7504 9.5C15.7504 9.57392 15.7359 9.64712 15.7076 9.7154C15.6793 9.78369 15.6378 9.84573 15.5855 9.89797Z"
                      fill="currentColor"
                    />
                  </svg>
                </button>
              </div>
            </div>
            <Slider ref={sliderRef} {...sliderSettings}>
              {genres.map((genre) => (
                <div key={genre._id} className="px-3">
                  <div className="position-relative d-inline-block w-100">
                    <div
                      onClick={() =>
                        playAndNavigate(
                          genre._id,
                          "genresong",
                          `/party-music/${genre._id}`,
                        )
                      }
                      style={{ cursor: "pointer" }}
                    >
                      <img
                        src={genre.themeImageUrl || `${process.env.PUBLIC_URL}/img/covers/cover.svg`}
                        alt={genre.name}
                        className="img-fluid rounded-3 mb-3 w-100"
                        style={{
                          height: "180px",
                          objectFit: "cover",
                          transition: "all 0.3s ease",
                        }}
                      />
                      <div
                        className="position-absolute top-50 start-50 translate-middle opacity-0"
                        style={{
                          transition: "all 0.3s ease",
                          pointerEvents: "none",
                          zIndex: 2,
                        }}
                      >
                        <button
                          className="btn btn-success rounded-circle shadow-lg"
                          style={{
                            width: "60px",
                            height: "60px",
                            backgroundColor: "#1DB954",
                            border: "none",
                          }}
                        >
                          Play
                        </button>
                      </div>
                      <div
                        className="position-absolute inset-0 rounded-3"
                        style={{
                          background: "rgba(0,0,0,0.5)",
                          transition: "opacity 0.3s ease",
                          opacity: 0,
                        }}
                        onMouseEnter={(e) => (e.target.style.opacity = 1)}
                        onMouseLeave={(e) => (e.target.style.opacity = 0)}
                      />
                    </div>
                    <Link
                      to={`/party-music/${genre._id}`}
                      className="text-decoration-none"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <h6 className="mb-1 text-capitalize text-black">
                        {genre.name}
                      </h6>
                    </Link>
                    <small className="text-muted d-block text-capitalize">
                      {genre.description}
                    </small>
                  </div>
                </div>
              ))}
            </Slider>
          </div>
        </div>
      </div>

      {/* SHARE MODAL */}
      {sharePopup.open && sharePopup.song && (
        <div
          className="modal-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
          onClick={() => setSharePopup({ open: false, song: null })}
        >
          <div
            className="share-popup"
            style={{
              backgroundColor: "#1a1a1a",
              padding: "25px",
              borderRadius: "12px",
              width: "90%",
              maxWidth: "350px",
              color: "white",
              border: "1px solid #333",
              boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ textAlign: "center", marginBottom: "25px" }}>
              <h4
                style={{
                  margin: "0 0 10px 0",
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#aaa7a7",
                }}
              >
                Share
              </h4>
              <p style={{ margin: 0, color: "#ccc", fontSize: "14px" }}>
                {sharePopup.song.title}
              </p>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "20px",
                marginBottom: "25px",
                flexWrap: "wrap",
              }}
            >
              {["facebook", "twitter", "whatsapp", "telegram"].map((plat) => (
                <button
                  key={plat}
                  onClick={() => shareOnSocialMedia(plat, sharePopup.song)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "12px",
                    color: "#ccc",
                  }}
                >
                  <div
                    style={{
                      width: "50px",
                      height: "50px",
                      backgroundColor:
                        plat === "facebook"
                          ? "#1877f2"
                          : plat === "twitter"
                            ? "#000000"
                            : plat === "whatsapp"
                              ? "#25d366"
                              : "#0088cc",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <i
                      className={`fab fa-${
                        plat === "twitter" ? "x-twitter" : plat
                      }`}
                      style={{ color: "white", fontSize: "20px" }}
                    ></i>
                  </div>{" "}
                  {plat.charAt(0).toUpperCase() + plat.slice(1)}
                </button>
              ))}
            </div>
            <div style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
              <input
                type="text"
                value={generateShareableUrl(sharePopup.song._id)}
                readOnly
                style={{
                  flex: 1,
                  padding: "12px 15px",
                  backgroundColor: "#2a2a2a",
                  border: "1px solid #444",
                  borderRadius: "8px",
                  color: "white",
                  fontSize: "14px",
                }}
              />
              <button
                onClick={copyLink}
                style={{
                  padding: "12px 20px",
                  backgroundColor: "#1DB954",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontWeight: "600",
                }}
              >
                COPY
              </button>
            </div>
            <button
              onClick={() => setSharePopup({ open: false, song: null })}
              style={{
                width: "100%",
                padding: "12px",
                backgroundColor: "transparent",
                color: "#ccc",
                border: "1px solid #444",
                borderRadius: "8px",
                cursor: "pointer",
              }}
            >
              CANCEL
            </button>
          </div>
        </div>
      )}

      {/* PLAYLIST MODAL */}
      {playlistModal.open && playlistModal.song && (
        <div
          className="modal-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
          onClick={() => setPlaylistModal({ open: false, song: null })}
        >
          <div
            className="playlist-popup"
            style={{
              backgroundColor: "#1a1a1a",
              padding: "25px",
              borderRadius: "12px",
              width: "90%",
              maxWidth: "400px",
              color: "white",
              border: "1px solid #333",
              boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ textAlign: "center", marginBottom: "20px" }}>
              <h4
                style={{
                  margin: "0 0 12px 0",
                  fontSize: "20px",
                  fontWeight: "600",
                }}
              >
                Add to Playlist
              </h4>
              <p style={{ margin: 0, color: "#ccc", fontSize: "16px" }}>
                {playlistModal.song.title}
              </p>
            </div>

            {!showCreateForm ? (
              <>
                <div
                  style={{
                    maxHeight: "250px",
                    overflowY: "auto",
                    marginBottom: "25px",
                  }}
                >
                  {playlists.length > 0 ? (
                    playlists.map((p) => {
                      const checked = selectedPlaylists.has(p._id);
                      return (
                        <label
                          key={p._id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            padding: "16px",
                            border: "1px solid #333",
                            borderRadius: "14px",
                            marginBottom: "12px",
                            cursor: "pointer",
                            backgroundColor: checked ? "#1DB95425" : "#222",
                            transition: "all 0.2s",
                          }}
                          onClick={() => togglePlaylistSelection(p._id)}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => {}}
                            style={{
                              marginRight: "16px",
                              transform: "scale(1.3)",
                            }}
                          />
                          <img
                            src={
                              p.playlistCoverUrl || "/img/live/songDefault.png"
                            }
                            alt={p.name}
                            style={{
                              width: "50px",
                              height: "50px",
                              borderRadius: "10px",
                              marginRight: "16px",
                              objectFit: "cover",
                            }}
                          />
                          <div style={{ flex: 1 }}>
                            <div
                              style={{
                                fontWeight: "600",
                                fontSize: "16px",
                              }}
                            >
                              {p.name}
                            </div>
                            {p.description && (
                              <small style={{ color: "#aaa" }}>
                                {p.description}
                              </small>
                            )}
                          </div>
                          {checked && (
                            <i
                              className="fas fa-check text-success ms-2"
                              style={{ fontSize: "20px" }}
                            ></i>
                          )}
                        </label>
                      );
                    })
                  ) : (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "40px",
                        color: "#888",
                      }}
                    >
                      No playlists yet
                    </div>
                  )}
                </div>

                <div className="d-flex gap-3">
                  <button
                    onClick={addToSelectedPlaylists}
                    disabled={selectedPlaylists.size === 0 || playlistLoading}
                    style={{
                      flex: 1,
                      padding: "16px",
                      backgroundColor: "#1DB954",
                      color: "white",
                      border: "none",
                      borderRadius: "14px",
                      cursor:
                        selectedPlaylists.size === 0
                          ? "not-allowed"
                          : "pointer",
                      fontWeight: "700",
                      opacity: selectedPlaylists.size === 0 ? 0.5 : 1,
                    }}
                  >
                    {playlistLoading
                      ? "Adding..."
                      : `Add to ${selectedPlaylists.size} Playlist${
                          selectedPlaylists.size > 1 ? "s" : ""
                        }`}
                  </button>
                  <button
                    onClick={() => setShowCreateForm(true)}
                    style={{
                      flex: 1,
                      padding: "16px",
                      backgroundColor: "transparent",
                      color: "#ccc",
                      border: "1px solid #555",
                      borderRadius: "14px",
                      cursor: "pointer",
                      fontWeight: "600",
                    }}
                  >
                    Create New
                  </button>
                </div>
              </>
            ) : (
              <>
                <input
                  className="form-control mb-3"
                  placeholder="Playlist Name *"
                  value={newPlaylist.name}
                  onChange={(e) =>
                    setNewPlaylist((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                  style={{
                    backgroundColor: "#2a2a2a",
                    border: "1px solid #444",
                    color: "white",
                    padding: "16px",
                    borderRadius: "12px",
                  }}
                />
                <textarea
                  className="form-control mb-3"
                  placeholder="Description (optional)"
                  rows="3"
                  value={newPlaylist.description}
                  onChange={(e) =>
                    setNewPlaylist((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  style={{
                    backgroundColor: "#2a2a2a",
                    border: "1px solid #444",
                    color: "white",
                    padding: "16px",
                    borderRadius: "12px",
                    resize: "none",
                  }}
                />
                <input
                  type="file"
                  accept="image/*"
                  className="form-control mb-4"
                  onChange={(e) =>
                    setNewPlaylist((prev) => ({
                      ...prev,
                      coverImage: e.target.files[0],
                    }))
                  }
                  style={{
                    backgroundColor: "#2a2a2a",
                    border: "1px solid #444",
                    color: "white",
                    padding: "12px",
                  }}
                />

                <div className="d-flex gap-3">
                  <button
                    onClick={createNewPlaylist}
                    disabled={playlistLoading}
                    style={{
                      flex: 1,
                      padding: "16px",
                      backgroundColor: "#1DB954",
                      color: "white",
                      border: "none",
                      borderRadius: "14px",
                      cursor: "pointer",
                      fontWeight: "700",
                    }}
                  >
                    {playlistLoading ? "Creating..." : "Create & Add"}
                  </button>
                  <button
                    onClick={() => setShowCreateForm(false)}
                    style={{
                      flex: 1,
                      padding: "16px",
                      backgroundColor: "transparent",
                      color: "#ccc",
                      border: "1px solid #555",
                      borderRadius: "14px",
                      cursor: "pointer",
                      fontWeight: "600",
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}

            <button
              onClick={() => setPlaylistModal({ open: false, song: null })}
              style={{
                width: "100%",
                marginTop: "20px",
                padding: "16px",
                backgroundColor: "transparent",
                color: "#ccc",
                border: "1px solid #555",
                borderRadius: "14px",
                cursor: "pointer",
                fontSize: "16px",
              }}
            >
              CLOSE
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default PlayMusic;
